import { RENDEZ_VOUS_KIND_LABELS, type RendezVousKind } from '@jeraisonne/db';
import type { VisioFeedRow } from './types';

export type VisioView = {
  /** Personne affichée en en-tête (l'autre participant, ou le titre de la réunion). */
  name: string;
  avatarUrl: string | null;
  /** « Cours », « Appel rapide », « Réunion »… */
  kindLabel: string;
  /** Titre libre d'une réunion (null pour un rendez-vous classique). */
  title: string | null;
  /** Durée réelle en minutes (fin réelle si le tuteur a terminé la réunion). */
  durationMin: number;
  /** Page du rendez-vous passé (dialogue, fichiers, résumé) ; null si aucune n'est accessible. */
  reservationId: string | null;
};

function kindLabelOf(kind: string | null): string {
  if (kind === 'reunion') return 'Réunion';
  if (kind && kind in RENDEZ_VOUS_KIND_LABELS) {
    return RENDEZ_VOUS_KIND_LABELS[kind as RendezVousKind];
  }
  return 'Visio';
}

/**
 * Présentation d'une visio passée selon qui regarde.
 * - Le tuteur voit l'élève (ou le titre de la réunion / « Cours collectif »).
 * - Un participant voit le tuteur.
 * - Lien vers `/rendez-vous/$reservationId` seulement quand la réservation est
 *   la sienne (tuteur du créneau ou élève titulaire) : dans un cours collectif,
 *   `reservation_id` est celle de l'élève qui a ouvert la salle, un autre élève
 *   n'y a pas accès. Une réunion libre n'a pas de réservation → pas de lien.
 */
export function visioFeedView(row: VisioFeedRow, viewerId: string | null | undefined): VisioView {
  const isTutor = !!viewerId && viewerId === row.tutor_id;
  const kindLabel = kindLabelOf(row.kind);
  const name = isTutor
    ? (row.student?.display_name ??
      row.title ??
      (row.kind === 'reunion' ? 'Réunion' : `${kindLabel} collectif`))
    : (row.tutor?.display_name ?? 'Ton prof');
  const avatarUrl = isTutor ? (row.student?.avatar_url ?? null) : (row.tutor?.avatar_url ?? null);

  const start = new Date(row.starts_at).getTime();
  const end = new Date(row.ended_at ?? row.ends_at).getTime();
  const durationMin = Math.max(0, Math.round((end - start) / 60_000));

  const ownsReservation =
    !!row.reservation_id && !!viewerId && (isTutor || row.student_id === viewerId);

  return {
    name,
    avatarUrl,
    kindLabel,
    title: row.kind === 'reunion' ? row.title : null,
    durationMin,
    reservationId: ownsReservation ? row.reservation_id : null,
  };
}
